/**
 * Managed Apps API client — wraps `/apps/*` routes served by the
 * AppsFleet RestApi (base-path mapped onto api-${stage}.zibby.app/apps/).
 *
 *   listCatalog()                → { items: [{ appType, displayName, ... }] }
 *   getCatalogEntry(appType)     → { appType, displayName, ... }
 *   deploy({ appType, projectId, name? })  → 202 { instanceId, url, ... }
 *   listInstances()              → { items: [{ instanceId, status, ... }] }
 *   getInstance(instanceId)      → { ... }
 *   destroy(instanceId)          → { instanceId, status: 'stopped' }
 *
 * Catalog browse is public (no JWT). Everything else requires the user
 * to be signed in.
 */

import api from './api';

const BASE = '/apps';

export const Apps = {
  // ── catalog (no auth) ───────────────────────────────────────────────
  listCatalog() {
    return api.get(`${BASE}/catalog`);
  },

  getCatalogEntry(appType) {
    if (!appType) return Promise.reject(new Error('appType is required'));
    return api.get(`${BASE}/catalog/${encodeURIComponent(appType)}`);
  },

  /**
   * Rate an app. Polymorphic on the same DDB table the workflow
   * marketplace uses — backend `apps.rateApp` upserts the user's row
   * and recomputes the META aggregate atomically.
   * @param {string} appType
   * @param {{rating: number, review?: string}} opts
   */
  rate(appType, { rating, review } = {}) {
    if (!appType) return Promise.reject(new Error('appType is required'));
    return api.post(`${BASE}/catalog/${encodeURIComponent(appType)}/ratings`, { rating, review });
  },

  // ── instances ───────────────────────────────────────────────────────
  /**
   * List deployed app instances. Optionally scoped to a project; the
   * backend has a ProjectCreatedAtIndex GSI it queries when `projectId`
   * is set (newest first), otherwise it returns all instances under the
   * caller's account.
   * @param {{ projectId?: string }} opts
   */
  listInstances({ projectId } = {}) {
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    return api.get(`${BASE}${qs}`);
  },

  getInstance(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}`);
  },

  /**
   * Deploy a marketplace app.
   * @param {string} appType    catalog id (e.g. "n8n")
   * @param {string} projectId  project to attach the instance to
   * @param {string} [name]     display name override
   * @param {'claude'|'codex'} [provider]  agent provider; default 'claude'
   * @param {'x86_64'|'arm64'} [architecture]  CPU arch; default = catalog's
   *   first listed arch (server picks). Omit to accept the operator-chosen
   *   default; only pass when the user explicitly picks one.
   * @returns 202 { instanceId, url, bridgeToken, mcpConfig, ... }
   */
  deploy({ appType, projectId, name, provider, architecture } = {}) {
    if (!appType)   return Promise.reject(new Error('appType is required'));
    if (!projectId) return Promise.reject(new Error('projectId is required'));
    const body = { appType, projectId };
    if (name) body.name = name;
    // Only send provider when the caller explicitly picked one — keeps
    // every pre-Codex consumer (CLI/MCP/old web bundle) request shape
    // byte-identical to backend default branch.
    if (provider) body.provider = provider;
    // Same gate for architecture: omit when the caller doesn't care, so
    // the server falls back to the catalog's first-listed arch (which is
    // also what the picker defaults to in the deploy modal — matches the
    // UX without trusting the client to mirror the catalog's preference).
    if (architecture) body.architecture = architecture;
    return api.post(`${BASE}/deploy`, body);
  },

  destroy(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    // Backend requires { confirm: true } — same shape as the Lambda
    // direct-invoke contract.
    // `delete` doesn't take a body arg in our client; pass via options.
    return api.delete(`${BASE}/${encodeURIComponent(instanceId)}`, { body: JSON.stringify({ confirm: true }) });
  },

  /**
   * Fetch a page of the instance's logs from CloudWatch. Backed by
   * FilterLogEvents over a per-instance stream prefix so results span
   * ALL ECS tasks for the instance (past + present, restart-survival).
   *
   * Pagination: pass `cursor` (the `nextToken` returned by a prior call)
   * to fetch the previous page (older lines). The frontend uses this
   * for infinite-scroll-to-top.
   *
   * Filtering: `filter` is forwarded to CloudWatch's filterPattern (a
   * plain substring also works). Empty/whitespace = no filter.
   *
   * @param {string} instanceId
   * @param {{ lines?: number, container?: 'agent-ops' | 'app', cursor?: string|null, filter?: string }} [opts]
   * @returns {Promise<{ instanceId, container, lines: string[], nextToken: string|null, hasMore: boolean }>}
   */
  getLogs(instanceId, { lines = 200, container, cursor, filter } = {}) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    const qs = new URLSearchParams({ lines: String(lines) });
    if (container) qs.set('container', container);
    if (cursor) qs.set('cursor', cursor);
    if (filter && filter.trim()) qs.set('filter', filter.trim());
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/logs?${qs.toString()}`);
  },

  /**
   * List the instance's structured agent run records (newest first).
   * Backed by the `zibby-{stage}-app-runs` DynamoDB table the agent-ops
   * daemon pushes to after every finished run — so this is independent
   * of CloudWatch retention and surfaces failed/timed-out runs too
   * (which emit no `conversation complete` log line).
   *
   * Heavy fields (systemPrompt / userPrompt / result) are omitted from
   * the list payload — fetch a single run via `getRun` to expand.
   *
   * Pagination: pass `cursor` (the `nextToken` returned by a prior call)
   * to fetch the next page of older runs.
   *
   * @param {string} instanceId
   * @param {{ limit?: number, cursor?: string|null }} [opts]
   * @returns {Promise<{ instanceId, runs: object[], nextToken: string|null, hasMore: boolean }>}
   */
  listRuns(instanceId, { limit = 50, cursor } = {}) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    const qs = new URLSearchParams({ limit: String(limit) });
    if (cursor) qs.set('cursor', cursor);
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/runs?${qs.toString()}`);
  },

  /**
   * Fetch a single agent run record in full — including the heavy
   * fields the list omits (`systemPrompt`, `userPrompt`, `result`).
   * Used to lazily populate an expanded row in the Agent activity tab.
   *
   * @param {string} instanceId
   * @param {string} runId
   * @returns {Promise<object>} the full run record
   */
  getRun(instanceId, runId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    if (!runId) return Promise.reject(new Error('runId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/runs/${encodeURIComponent(runId)}`);
  },

  /**
   * Live EFS usage for the instance. Backed by CloudWatch's AWS/EFS
   * StorageBytes metric (per-AccessPoint dimension; published ~every
   * 15min by EFS so brand-new deploys return `usedBytes: null` until
   * the first sample lands).
   *
   * @param {string} instanceId
   * @returns {Promise<{ instanceId, usedBytes: number|null, storageClass: string, sampleAt: string|null }>}
   */
  getStorage(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/storage`);
  },

  /**
   * In-place upgrade: swap the agent-ops container image, preserving EFS
   * data + bridgeToken + ALB wiring. Re-registers the per-instance task
   * def with a new revision, then force-redeploys the ECS Service.
   *
   * `version` (optional) overrides the agent-ops image tag. Without it,
   * the upgrade picks up whatever's in the AppsFleet base task def in
   * SSM (which CDK has bumped).
   *
   * @param {string} instanceId
   * @param {{ version?: string }} [opts]
   * @returns {Promise<{ instanceId, status: 'upgrading', taskDefinitionArn, imageOverride: string|null }>}
   */
  upgrade(instanceId, { version } = {}) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    const qs = version ? `?version=${encodeURIComponent(version)}` : '';
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/upgrade${qs}`);
  },

  /**
   * Safe, read-only view of the per-instance env block. Drives the
   * "ENV" tab on AppDetailPage.
   *
   * Privacy: backend allow-lists which keys are returned at all, and
   * masks every sensitive value to its 6-char suffix. The plaintext of
   * CLAUDE_CODE_OAUTH_TOKEN / ANTHROPIC_API_KEY NEVER crosses this wire.
   *
   * @param {string} instanceId
   * @returns {Promise<{ instanceId, identity: Array<{key,value}>, credentials: Array<{key,present,suffix}> }>}
   */
  getEnv(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}/env`);
  },

  /**
   * Rotate the per-instance Claude credential to whatever is currently
   * stored in the user's workspace-credentials. Backend re-reads the
   * encrypted row, re-registers the per-instance task def with the new
   * env, and force-redeploys the ECS Service. EFS preserved.
   *
   * @param {string} instanceId
   * @returns {Promise<{ instanceId, mode, taskDefinitionArn, message }>}
   */
  updateCredential(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.post(`${BASE}/${encodeURIComponent(instanceId)}/credentials`, {});
  },
};
